import { apiClient } from './client';

// ── Tipos ─────────────────────────────────────────────────────────────────────

export type CropType =
  | 'MAIZ' | 'TOMATE' | 'CHILE' | 'PEPINO' | 'AGUACATE'
  | 'BROCOLI' | 'ESPINACA' | 'UVA' | 'FRESA' | 'OTRO';

export type FenologiaStage =
  | 'PREPARACION_SUELO' | 'SIEMBRA' | 'GERMINACION' | 'EMERGENCIA'
  | 'ESTABLECIMIENTO' | 'VEGETATIVO' | 'FLORACION' | 'FRUCTIFICACION'
  | 'MADURACION' | 'COSECHA' | 'POST_COSECHA';

export type PestPressureLevel = 'BAJO' | 'MEDIO' | 'ALTO' | 'CRITICO';
export type PestType          = 'PLAGA' | 'ENFERMEDAD' | 'MALEZA';

export interface PhenologyRecord {
  recordId:   string;
  tenantId:   string;
  loteId:     string;
  ciclo:      string;
  cultivo:    CropType;
  cropId?:    string;
  /** Zona (módulo) del lote a la que pertenece el registro. */
  moduleId?:  string;
  stage:      FenologiaStage;
  recordedAt: string;
  recordedBy: string;
  recordedByName?: string | null;
  dapActual?: number;
  notes?:     string;
}

export interface PestRecord {
  recordId:     string;
  tenantId:     string;
  loteId:       string;
  ciclo:        string;
  moduleId?:    string;
  pestType:     PestType;
  name:         string;
  pressure:     PestPressureLevel;
  affectedPct?: number;
  recordedAt:   string;
  recordedBy:   string;
  recordedByName?: string | null;
  actionTaken?: string;
  notes?:       string;
}

export interface ScoutingObservation {
  organism:       string;
  stage?:         string;
  plantsAffected: number;
}

export interface ScoutingPoint {
  pointNumber:  number;
  sampleSize:   number;
  observations: ScoutingObservation[];
}

export interface ScoutingReport {
  recordId:     string;
  loteId:       string;
  ciclo:        string;
  recordedAt:   string;
  recordedBy:   string;
  recordedByName?: string | null;
  points:       ScoutingPoint[];
  pestType:     PestType;
  pressure:     PestPressureLevel;
  affectedPct:  number;
  notes?:       string;
  evidence?:    { photoUrls?: string[] };
}

// ── Inputs de mutaciones ──────────────────────────────────────────────────────

export interface CreatePhenologyInput {
  ciclo:     string;
  /** Con `cropId` el backend deriva el cultivo del catálogo; sin él, `cultivo` es obligatorio. */
  cultivo?:  CropType;
  cropId?:   string;
  /** Zona del lote a la que pertenece el registro (opcional). */
  moduleId?: string;
  stage:     FenologiaStage;
  dapActual?: number;
  notes?:    string;
}

export interface CreatePestInput {
  ciclo:        string;
  /** Zona del lote (opcional). */
  moduleId?:    string;
  pestType:     PestType;
  name:         string;
  pressure:     PestPressureLevel;
  affectedPct?: number;
  actionTaken?: string;
  notes?:       string;
}

// ── Queries ───────────────────────────────────────────────────────────────────

/** Última etapa fenológica por lote (lotes sin registros no aparecen). Alimenta el tema "Etapa fenológica" del mapa. */
export interface LatestStageEntry { stage: FenologiaStage; ciclo: string; cultivo: string; recordedAt: string }
export const fetchLatestStages = async (
  ranchoId?: string | null,
): Promise<Record<string, LatestStageEntry>> =>
  (await apiClient.get('/api/phenology/latest-stage', { params: ranchoId ? { ranchoId } : undefined })).data;

export const fetchPhenology = async (
  loteId: string,
  ciclo: string,
  moduleId?: string,
): Promise<PhenologyRecord[]> =>
  (await apiClient.get(`/api/lotes/${loteId}/phenology`, { params: { ciclo, ...(moduleId ? { moduleId } : {}) } })).data;

export const fetchPests = async (
  loteId: string,
  ciclo: string,
  moduleId?: string,
): Promise<PestRecord[]> =>
  (await apiClient.get(`/api/lotes/${loteId}/pests`, { params: { ciclo, ...(moduleId ? { moduleId } : {}) } })).data;

export const fetchPestAlerts = async (
  loteId: string,
  ciclo: string
): Promise<PestRecord[]> =>
  (await apiClient.get(`/api/lotes/${loteId}/pest-alerts`, { params: { ciclo } })).data;

export const fetchScouting = async (
  loteId: string,
  ciclo: string
): Promise<ScoutingReport[]> =>
  (await apiClient.get(`/api/lotes/${loteId}/scouting`, { params: { ciclo } })).data;

// ── Mutaciones ────────────────────────────────────────────────────────────────

export const createPhenologyRecord = async (
  loteId: string,
  input: CreatePhenologyInput
): Promise<PhenologyRecord> =>
  (await apiClient.post(`/api/lotes/${loteId}/phenology`, input)).data;

export const createPestRecord = async (
  loteId: string,
  input: CreatePestInput
): Promise<PestRecord> =>
  (await apiClient.post(`/api/lotes/${loteId}/pests`, input)).data;

export const deletePhenologyRecord = async (loteId: string, recordId: string): Promise<void> =>
  (await apiClient.delete(`/api/lotes/${loteId}/phenology/${recordId}`)).data;

export const deletePestRecord = async (loteId: string, recordId: string): Promise<void> =>
  (await apiClient.delete(`/api/lotes/${loteId}/pests/${recordId}`)).data;

export const deleteScoutingReport = async (loteId: string, recordId: string): Promise<void> =>
  (await apiClient.delete(`/api/lotes/${loteId}/scouting/${recordId}`)).data;

export interface LoteCycle {
  ciclo:  string;
  lastAt: string;
  /** true si el ciclo existe como entidad (Agrovid_CropCycles); false = solo string legado. */
  isEntity?: boolean;
}

/** Ciclos conocidos de un lote (fenología, riego, presupuestos, actividades), más reciente primero */
export const fetchLoteCycles = async (loteId: string): Promise<LoteCycle[]> =>
  (await apiClient.get(`/api/lotes/${loteId}/cycles`)).data;


// ── Cultivo y siembra (lote sin zonas o zona) ────────────────────────────────

export interface CropAssignment { cropId?: string | null; plantingDate?: string | null }

export const setLoteCrop = async (loteId: string, body: CropAssignment): Promise<void> => {
  await apiClient.patch(`/api/lotes/${loteId}/crop`, body);
};

export const setModuleCrop = async (moduleId: string, body: { cropId?: string | null; plantingDate?: string }): Promise<void> => {
  await apiClient.patch(`/api/modules/${moduleId}/crop`, body);
};

// ── Ciclos de cultivo (entidad) ──────────────────────────────────────────────

export interface CropCycle {
  targetId: string;
  ciclo: string;
  targetType: 'MODULE' | 'LOTE';
  loteId: string;
  cropId: string;
  plantingDate?: string;
  status: 'OPEN' | 'CLOSED';
  startedAt: string;
  startedBy?: string;
  closedAt?: string;
  closedBy?: string;
  /** Nombres legibles que agrega el GET (Task 13); pueden faltar. */
  startedByName?: string;
  closedByName?: string;
}

export const fetchLoteCropCycles = async (loteId: string): Promise<CropCycle[]> =>
  (await apiClient.get(`/api/lotes/${loteId}/crop-cycles`)).data;

export const startCropCycle = async (body: {
  targetType: 'MODULE' | 'LOTE'; targetId: string; ciclo?: string; cropId: string; plantingDate?: string;
}): Promise<CropCycle> => (await apiClient.post('/api/crop-cycles', body)).data;

export const closeCropCycle = async (body: { targetId: string; ciclo: string }): Promise<void> => {
  await apiClient.post('/api/crop-cycles/close', body);
};
